import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import FacilityMap from './components/FacilityMap';
import MemberSpotlight from './components/MemberSpotlight';
import LocationAchievements from './components/LocationAchievements';

const MTTDemoGymLocation = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading } = useAuth();
  const [gymInfo, setGymInfo] = useState(null);
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeClasses: 0,
    trainers: 0
  });
  const [loading, setLoading] = useState(true);

  const amenities = [
    { icon: 'Clock', label: '24/7 Access', description: 'Train anytime, day or night' },
    { icon: 'Cpu', label: 'Smart Equipment', description: 'AI-powered workout tracking' },
    { icon: 'Video', label: 'Virtual Training', description: 'Live and on-demand classes' },
    { icon: 'Heart', label: 'Recovery Zone', description: 'Massage and stretching area' },
    { icon: 'Apple', label: 'Nutrition Bar', description: 'Healthy meals and smoothies' },
    { icon: 'Smartphone', label: 'Tech Integration', description: 'Seamless app connectivity' }
  ];

  const operatingHours = [
    { day: 'Monday - Sunday', hours: 'Open 24 Hours' }
  ];

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id) {
      loadGymInfo();
      loadStats();
    }
  }, [currentTenant]);

  const loadGymInfo = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('tenants')
        ?.select('*, tenant_settings(*)')
        ?.eq('slug', 'mtt-demo-gym')
        ?.single();

      if (error) throw error;

      setGymInfo(data);
    } catch (error) {
      console.error('Error loading gym info:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Load members count
      const { count: membersCount } = await supabase
        ?.from('tenant_members')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('tenant_id', currentTenant?.id);

      // Load classes count
      const { count: classesCount } = await supabase
        ?.from('classes')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('scheduled_at', new Date()?.toISOString());

      // Load trainers count
      const { count: trainersCount } = await supabase
        ?.from('tenant_members')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('role', 'trainer');

      setStats({
        totalMembers: membersCount || 0,
        activeClasses: classesCount || 0,
        trainers: trainersCount || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading gym location...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="mx-auto max-w-[1440px] px-4 md:px-6 py-6 md:py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-primary to-accent rounded-2xl p-8 md:p-12 mb-8 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">MTT Demo Gym</h1>
              <p className="text-lg mb-4 opacity-90">
                Experience the future of fitness with cutting-edge technology and world-class facilities
              </p>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Icon name="MapPin" size={18} />
                  <span>321 Technology Drive, Austin, TX 78701</span>
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <Button variant="secondary" className="flex items-center gap-2">
                <Icon name="Phone" size={18} />
                Contact
              </Button>
              <Button variant="secondary" className="flex items-center gap-2">
                <Icon name="Navigation" size={18} />
                Directions
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
          <div className="bg-card rounded-xl shadow-md p-6 text-center">
            <Icon name="Users" size={32} className="text-primary mx-auto mb-3" />
            <p className="text-3xl font-bold text-foreground mb-1">{stats?.totalMembers}</p>
            <p className="text-sm text-muted-foreground">Active Members</p>
          </div>
          <div className="bg-card rounded-xl shadow-md p-6 text-center">
            <Icon name="Calendar" size={32} className="text-success mx-auto mb-3" />
            <p className="text-3xl font-bold text-foreground mb-1">{stats?.activeClasses}</p>
            <p className="text-sm text-muted-foreground">Upcoming Classes</p>
          </div>
          <div className="bg-card rounded-xl shadow-md p-6 text-center">
            <Icon name="Award" size={32} className="text-accent mx-auto mb-3" />
            <p className="text-3xl font-bold text-foreground mb-1">{stats?.trainers}</p>
            <p className="text-sm text-muted-foreground">Expert Trainers</p>
          </div>
        </div>

        {/* Amenities Section */}
        <div className="bg-card rounded-xl shadow-md p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-6">World-Class Amenities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {amenities?.map((amenity, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10">
                  <Icon name={amenity?.icon} size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{amenity?.label}</h3>
                  <p className="text-sm text-muted-foreground">{amenity?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Operating Hours */}
        <div className="bg-card rounded-xl shadow-md p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-6">Operating Hours</h2>
          <div className="space-y-3">
            {operatingHours?.map((schedule, index) => (
              <div key={index} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                <span className="font-medium text-foreground">{schedule?.day}</span>
                <span className="text-success font-semibold">{schedule?.hours}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Facility Map */}
        <FacilityMap />

        {/* Member Spotlight & Achievements */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8">
          <MemberSpotlight />
          <LocationAchievements />
        </div>

        {/* Contact Information */}
        <div className="bg-card rounded-xl shadow-md p-6 md:p-8">
          <h2 className="text-2xl font-bold text-foreground mb-6">Get in Touch</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <Icon name="Phone" size={24} className="text-primary" />
              <div>
                <p className="font-semibold text-foreground mb-1">Phone</p>
                <p className="text-sm text-muted-foreground">+1 (512) 555-0104</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Icon name="Mail" size={24} className="text-primary" />
              <div>
                <p className="font-semibold text-foreground mb-1">Email</p>
                <p className="text-sm text-muted-foreground">info@mttdemogym.com</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Icon name="Globe" size={24} className="text-primary" />
              <div>
                <p className="font-semibold text-foreground mb-1">Website</p>
                <p className="text-sm text-muted-foreground">mttdemogym.com</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default MTTDemoGymLocation;